import _ from "underscore";
import { defineStore } from "pinia";

import { ModuleStatus } from "@/models/ModuleStatus";

import { notesService } from "@/services";
import { isApiError } from "@/services/Api";

import { useBrandStore } from "@/stores/brand-store";
import { useCaseStore } from "@/stores/case-store";

import type { Note } from "@/models/Note";

interface State {
  notes: readonly Note[];
  moduleStatus: ModuleStatus;
  notesObject: Record<string, readonly Note[]>;
}
export const useNotesStore = defineStore("notes", {
  scope: "case",
  state: (): State => ({
    notes: [],
    moduleStatus: ModuleStatus.Initial,
    notesObject: {},
  }),
  getters: {},
  actions: {
    async load() {
      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      this.setStatus(ModuleStatus.Loading);

      let notes: Note[] = [];
      try {
        notes = await notesService.getAllNotes(brandStore.selectedClient, brandStore.selectedBrand, selectedCaseName);
      } catch (e) {
        if (isApiError(e) && e.status !== 204) {
          throw e;
        }
      } finally {
        this.notesLoaded(notes || []);
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async loadForFile(payload: { fileReference: string }) {
      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      this.setStatus(ModuleStatus.Loading);
      let notes: Note[] = [];
      try {
        notes = await notesService.getAllNotesForFile(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.fileReference,
        );
      } catch (e) {
        if (isApiError(e) && e.status !== 204) {
          throw e;
        }
      } finally {
        this.notesLoadedForFile({
          fileReference: payload.fileReference,
          notes: notes || [],
        });
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async saveNote(payload: { fileReference: string; noteText: string }): Promise<void> {
      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      this.setStatus(ModuleStatus.Loading);
      try {
        await notesService.saveNote(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.fileReference,
          { noteText: payload.noteText },
        );
        await this.loadForFile({ fileReference: payload.fileReference });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },
    async deleteNote(payload: { fileReference: string; externalReference: string }) {
      const brandStore = useBrandStore();
      const selectedCaseName = useCaseStore().selectedCaseName;
      if (brandStore.selectedBrand == null || brandStore.selectedClient == null || selectedCaseName == null) return;

      this.setStatus(ModuleStatus.Loading);
      try {
        await notesService.deleteNote(
          brandStore.selectedClient,
          brandStore.selectedBrand,
          selectedCaseName,
          payload.fileReference,
          payload.externalReference,
        );
        await this.loadForFile({ fileReference: payload.fileReference });
      } finally {
        this.setStatus(ModuleStatus.Ready);
      }
    },

    // Mutations

    setStatus(moduleStatus: ModuleStatus): void {
      this.moduleStatus = moduleStatus;
    },
    notesLoaded(payload: Note[]): void {
      this.notes = Object.freeze(payload);
      this.notesObject = Object.freeze(_.groupBy(this.notes, "fileReference"));
    },
    notesLoadedForFile(payload: { fileReference: string; notes: Note[] }): void {
      this.notes = Object.freeze(
        this.notes.filter((n) => n.fileReference !== payload.fileReference).concat(payload.notes),
      );
      this.notesObject = Object.freeze(_.groupBy(this.notes, "fileReference"));
    },
    reset(): void {
      this.moduleStatus = ModuleStatus.Initial;
      this.notes = [];
      this.notesObject = {};
    },
  },
});
